import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { usePopper } from 'react-popper';
import classNames from 'classnames';

import { PlayerState } from './playerState';

import { useOutsideClick, useDelayedUnmount } from '../hooks';
import { CheckSVGIcon } from '../svg';

import '../menu/menu.scss';
import './player.scss';

type Props = {
  model: PlayerState;
  className?: string;
  offsetY?: number;
  anchorElement?: Element | null;
};

export const PlaybackRate = observer((props: Props) => {
  const { model } = props;
  const buttonLabel = `${model.playbackRate}x`;
  const offsetY = props?.offsetY || 0;
  const className = classNames(
    'playback-rate-btn',
    props?.className
  );

  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [forceClose, setForceClose] = useState(false);
  const { shouldRender, isAnimating } = useDelayedUnmount(isOpened && !forceClose, forceClose ? 0 : 400);

  // Listen for custom event to close this menu when another opens
  useEffect(() => {
    const handleCloseAll = () => {
      if (isOpened) {
        setForceClose(true);
        setIsOpened(false);
      }
    };
    document.addEventListener('dropdown:close-all', handleCloseAll);
    return () => document.removeEventListener('dropdown:close-all', handleCloseAll);
  }, [isOpened]);

  // Reset forceClose when menu is fully closed
  useEffect(() => {
    if (!isOpened && forceClose) {
      setForceClose(false);
    }
  }, [isOpened, forceClose]);

  const popperAnchorElement = props?.anchorElement || anchorElement;

  const { styles, attributes } = usePopper(popperAnchorElement, popperElement, {
    placement: 'top-end',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, offsetY],
        }
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left-start', 'bottom', 'bottom-end'],
        },
      }
    ],
  });

  useOutsideClick(popperElement, () => setIsOpened(false));

  return (
    <>
      <button className={className}
        ref={setAnchorElement}
        onClick={evt => {
          if (!isOpened) {
            document.dispatchEvent(new CustomEvent('dropdown:close-all'));
          }
          setIsOpened(!isOpened);
          evt.stopPropagation();
        }}>
        <span className="text">
          {buttonLabel}
        </span>
      </button>

      {shouldRender && (
        <div className={classNames('playback-rate-popup menu-popup popup menu-animate', isAnimating)}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}>
          <div className="menu-items"
            onClick={() => setIsOpened(false)}>
            {model.playbackRateOptions.map(({ label, rate}) => (
              <button key={label}
                className="menu-item-btn btn-core"
                onClick={model.invokeHandler('setPlaybackRate', { rate })}>
                <span className="icon">
                  {model.playbackRate === rate && <CheckSVGIcon />}
                </span>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
});
